import React from 'react';
import './HowItWorks.css';

export const HowItWorks: React.FC = () => {
  return (
    <div className="how-it-works-container">
      <div className="how-it-works-content">
        <h1>How Unreleased Tracks Works</h1>
        
        <p className="intro-text">
          Unreleased Tracks is a music feedback platform designed to help artists get quality feedback on their tracks. 
          Our karma-based system ensures fair participation and prioritizes active community members.
        </p>

        <section className="hiw-section">
          <div className="section-icon">⭐</div>
          <h2>The Karma System</h2>
          <p>
            Karma is the currency that powers our feedback community. It's designed to reward active participation 
            and ensure everyone contributes to the ecosystem.
          </p>
          
          <div className="karma-details">
            <div className="karma-item">
              <h3>Earning Karma 💚</h3>
              <p>
                <strong>Give feedback to earn karma.</strong> Every time you provide thoughtful feedback 
                on another artist's track, you earn karma points. The more feedback you give, the more 
                karma you accumulate.
              </p>
            </div>
            
            <div className="karma-item">
              <h3>Spending Karma 💸</h3>
              <p>
                <strong>Receiving feedback costs karma.</strong> When your track receives feedback from 
                other artists, it costs you karma points. This ensures that everyone contributes back to 
                the community.
              </p>
            </div>
          </div>

          <div className="karma-highlight">
            <h3>Ranking by Karma 🏆</h3>
            <p>
              The <strong>Give Feedback page is ranked by artist karma</strong>. Artists with higher karma 
              appear higher in the list, meaning they get more visibility and more feedback on their tracks. 
              This rewards active community members who regularly give feedback to others.
            </p>
            <p className="tip">
              <strong>Tip:</strong> The more feedback you give, the more feedback you'll get!
            </p>
          </div>
        </section>

        <section className="hiw-section">
          <div className="section-icon">🎵</div>
          <h2>Track Review System</h2>
          <p>
            Each artist has <strong>one track under review at a time</strong>. This focused approach ensures 
            quality feedback and prevents the platform from becoming overwhelming.
          </p>
          
          <div className="track-details">
            <h3>Managing Your Track Under Review</h3>
            <p>
              You can update which track is under review at any time by visiting the{' '}
              <strong>My Tracks page</strong>. Simply select a different track from your uploaded tracks, 
              and it will become your active track for feedback.
            </p>
            <ul>
              <li>Upload one or more tracks to your library</li>
              <li>Choose one track to be under review. The last track you uploaded is the default.</li>
              <li>Switch tracks whenever you want new feedback</li>
              <li>Previous feedback remains saved for each track</li>
            </ul>
          </div>
        </section>

        <section className="hiw-section">
          <div className="section-icon">🔄</div>
          <h2>How the Process Works</h2>
          <ol className="process-steps">
            <li>
              <strong>Upload Your Tracks:</strong> Upload one or more tracks
            </li>
            <li>
              <strong>Select Track for Review:</strong> Choose which track should be under review, or default to the last track uploaded
            </li>
            <li>
              <strong>Give Feedback:</strong> Provide thoughtful feedback on other artists' tracks to earn karma
            </li>
            <li>
              <strong>Rise in Rankings:</strong> Your karma increases, moving you higher in the Give Feedback list
            </li>
            <li>
              <strong>Receive Feedback:</strong> Other artists discover your track and provide feedback
            </li>
            <li>
              <strong>Repeat:</strong> Continue the cycle with new or updated tracks
            </li>
          </ol>
        </section>
      </div>
    </div>
  );
};

export default HowItWorks;
